import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/shared/lib/supabase';

export interface OutroEvento {
  id:              number;
  fixture_id:      number;
  time_casa:       string;
  time_visitante:  string;
  escudo_casa:     string | null;
  escudo_visitante: string | null;
  liga_nome:       string | null;
  placar_casa:     number;
  placar_visitante: number;
  tipo:            'gol' | 'cartao_vermelho';
  time_evento:     'casa' | 'visitante';
  jogador:         string;
  minuto:          number;
}

const DURACAO_NOTIF_MS = 7_000; // cada notificação fica visível por 7s

/**
 * Escuta futebol_outros_eventos via Realtime e mantém uma fila FIFO.
 * Exibe uma notificação por vez — a próxima só entra após a atual sair.
 */
export function useOutrosEventos() {
  const [atual,  setAtual]  = useState<OutroEvento | null>(null);
  const filaRef             = useRef<OutroEvento[]>([]);
  const timerRef            = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exibindoRef         = useRef(false);

  const proximaDaFila = useCallback(() => {
    if (filaRef.current.length === 0) {
      exibindoRef.current = false;
      setAtual(null);
      return;
    }
    const proximo = filaRef.current.shift()!;
    exibindoRef.current = true;
    setAtual(proximo);

    timerRef.current = setTimeout(() => {
      setAtual(null);
      // Pequena pausa entre notificações para não parecer instantâneo
      timerRef.current = setTimeout(proximaDaFila, 400);
    }, DURACAO_NOTIF_MS);
  }, []);

  const enfileirar = useCallback((ev: OutroEvento) => {
    filaRef.current.push(ev);
    if (!exibindoRef.current) {
      proximaDaFila();
    }
  }, [proximaDaFila]);

  useEffect(() => {
    const channel = supabase
      .channel('futebol-outros-eventos')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'futebol_outros_eventos' },
        (payload) => enfileirar(payload.new as OutroEvento),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enfileirar]);

  return { atual };
}
